import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { getSessionId } from '@/utils/getSessionId';

export const useDoorsaleStore = defineStore('doorsale', () => {
	const selectedEventIds = ref<(number | string)[]>([]);
	const sessionId = ref<string | null>(null);
	const collectCustomerInfo = ref<boolean>(true);

	const isActive = computed<boolean>(() => sessionId.value !== null);
	const selectedCount = computed<number>(() => selectedEventIds.value.length);

	const STORAGE_KEY = 'doorsaleSelectedEventIds';
	const TTL_MS = 20 * 60 * 1000;

	function init() {
		sessionId.value = getSessionId();
		if (sessionId.value) {
			lastSessionConfirmedAt = Date.now();
			sessionKnownExpired = false;
			console.log('[doorsale] init: session confirmed at', new Date(lastSessionConfirmedAt).toISOString(), '— expires after', SESSION_SERVER_TTL_MS / 1000, 's');
		}
		const stored = localStorage.getItem(STORAGE_KEY);
		if (stored) {
			try {
				const parsed = JSON.parse(stored);
				const ids = parsed?.ids;
				const savedAt = parsed?.savedAt;
				if (Array.isArray(ids) && typeof savedAt === 'number' && Date.now() - savedAt <= TTL_MS) {
					selectedEventIds.value = ids;
					localStorage.setItem(STORAGE_KEY, JSON.stringify({ ids, savedAt: Date.now() }));
				} else {
					localStorage.removeItem(STORAGE_KEY);
				}
			} catch {
				localStorage.removeItem(STORAGE_KEY);
			}
		}
	}

	function toggleEvent(id: number | string) {
		const index = selectedEventIds.value.indexOf(id);
		if (index === -1) {
			selectedEventIds.value.push(id);
		} else {
			selectedEventIds.value.splice(index, 1);
		}
		localStorage.setItem(STORAGE_KEY, JSON.stringify({ ids: selectedEventIds.value, savedAt: Date.now() }));
	}

	function clearEvents() {
		selectedEventIds.value = [];
		localStorage.removeItem(STORAGE_KEY);
	}

	function reset() {
		selectedEventIds.value = [];
		localStorage.removeItem(STORAGE_KEY);
		// collectCustomerInfo is intentionally NOT reset — it is a persistent user setting
	}

	function setCollectCustomerInfo(value: boolean) {
		collectCustomerInfo.value = value;
	}

	// SESSION_SERVER_TTL_MS must match the TTL in Session.pm (currently 3600 s = 1 hour).
	// LIMITATION (clock math): lastSessionConfirmedAt is set when the session is explicitly
	// confirmed (init or a successful keepalive response). isSessionExpired() is a one-directional
	// guarantee — if true the session IS expired, but the inverse does not hold: the server may
	// invalidate the session for other reasons (admin logout elsewhere, server restart).
	// A truly reliable "still alive?" check requires a read-only server ping.
	const SESSION_SERVER_TTL_MS = 60 * 60 * 1000;
	let lastSessionConfirmedAt = 0;
	// Set to true when the keepalive endpoint confirms the session is gone,
	// so isSessionExpired() returns true immediately without waiting for the clock.
	let sessionKnownExpired = false;

	function isSessionExpired(): boolean {
		if (sessionKnownExpired) return true;
		if (lastSessionConfirmedAt === 0) return false;
		return Date.now() - lastSessionConfirmedAt >= SESSION_SERVER_TTL_MS;
	}

	function handleSessionExpired(): void {
		console.log('[doorsale] handleSessionExpired: redirecting to /admin/login. lastSessionConfirmedAt:', lastSessionConfirmedAt ? new Date(lastSessionConfirmedAt).toISOString() : 'never', '| sessionKnownExpired:', sessionKnownExpired);
		sessionKnownExpired = false;
		reset();
		sessionId.value = null;
		window.location.href = '/admin/login';
	}

	// Returns true if the admin session cookie is present; otherwise redirects
	// to /admin/login and returns false. Call this at the top of any doorsale
	// route guard before doing any other work.
	function requireAdminSession(): boolean {
		if (getSessionId()) return true;
		window.location.href = '/admin/login';
		return false;
	}

	const KEEPALIVE_RATE_LIMIT_MS = 15 * 60 * 1000;
	let lastKeepaliveTs = 0;

	async function sessionKeepalive() {
		if (!isActive.value) return;
		if (Date.now() - lastKeepaliveTs < KEEPALIVE_RATE_LIMIT_MS) return;
		console.log('[doorsale] sessionKeepalive: firing keepalive request');
		lastKeepaliveTs = Date.now();
		try {
			const res = await fetch('/of2/private_services/session_keepalive', {
				headers: { 'X-Requested-With': 'XMLHttpRequest' },
			});
			const data = await res.json();
			console.log('[doorsale] sessionKeepalive: response', data);
			if (data.ok === 1) {
				lastSessionConfirmedAt = Date.now();
				sessionKnownExpired = false;
			} else {
				// Don't redirect here — the current page may not be a doorsale route.
				// Set the flag; the periodic interval will redirect within 60s if the
				// user is on a doorsale page.
				console.log('[doorsale] sessionKeepalive: session expired per server response, setting sessionKnownExpired');
				sessionKnownExpired = true;
			}
		} catch (e) {
			// Network error — don't redirect, let the clock-math timer handle it
			console.warn('[doorsale] sessionKeepalive: network error', e);
		}
	}

	// Read-only session check — does NOT refresh the server-side TTL.
	// Called by the periodic interval so idle users are not kept logged in.
	async function checkAdminSession() {
		if (!isActive.value) return;
		try {
			const res = await fetch('/of2/private_services/session_check', {
				headers: { 'X-Requested-With': 'XMLHttpRequest' },
			});
			const data = await res.json();
			console.log('[doorsale] checkAdminSession: response', data);
			if (data.ok !== 1) {
				sessionKnownExpired = true;
			}
		} catch (e) {
			// Network error — don't redirect, let the clock-math timer handle it
			console.warn('[doorsale] checkAdminSession: network error', e);
		}
	}

	return {
		selectedEventIds,
		sessionId,
		collectCustomerInfo,
		isActive,
		selectedCount,
		init,
		toggleEvent,
		clearEvents,
		reset,
		setCollectCustomerInfo,
		sessionKeepalive,
		checkAdminSession,
		isSessionExpired,
		handleSessionExpired,
		requireAdminSession,
	};
});
